import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { OrderMediaItem } from "@/lib/order-media"

// Funscript action axes - maps to T-code and buttplug.io output types
// All fields are optional - scripts without them work exactly as before
export type ScriptAxis = 
  | 'stroke'      // L0 - main up/down (pos field, existing)
  | 'vibration'   // V0 - vibration intensity
  | 'rotation'    // R0 - rotation speed/direction
  | 'oscillation' // Oscillate - fucking machines
  | 'air'         // Constrict - suction/air pumps
  | 'surge'       // L1 - forward/back (OSR2/SR6)
  | 'sway'        // L2 - left/right (OSR2/SR6)
  | 'roll'        // R1 - tilt side to side (OSR2/SR6)
  | 'pitch'       // R2 - tilt forward/back (OSR2/SR6)

export interface FunscriptAction {
  at: number // milliseconds
  pos: number // 0-100 position (stroke / L0)
  speed?: number // optional: velocity/speed (used in y-only mode)
  // Multi-axis fields - all optional, additive
  vibration?: number   // 0-100 vibration intensity (V0)
  rotation?: number    // 0-100 rotation speed (R0), 50 = stopped, >50 = clockwise
  oscillation?: number // 0-100 oscillation speed (fucking machines)
  air?: number         // 0-100 suction/constrict level
  surge?: number       // 0-100 forward/back position (L1, OSR2/SR6)
  sway?: number        // 0-100 left/right position (L2, OSR2/SR6)
  roll?: number        // 0-100 roll tilt (R1, OSR2/SR6)
  pitch?: number       // 0-100 pitch tilt (R2, OSR2/SR6)
}

export interface FunscriptMetadata {
  creator?: string
  description?: string
  duration?: number
  license?: string
  notes?: string
  performers?: string[]
  script_url?: string
  tags?: string[]
  title?: string
  type?: string
  video_url?: string
  range?: number
  version?: string
  // When true, `duration` is intentionally longer than the source clip's own length —
  // playback should loop the clip to fill the script instead of stopping when the clip ends.
  // Built for short GIFs/loops (e.g. a 1s clip scripted out to 30s), but works for any length.
  loopSource?: boolean
}

export interface Funscript {
  version: string
  inverted?: boolean
  range?: number
  metadata?: FunscriptMetadata
  actions: FunscriptAction[]
}

// Pattern types
export interface ScriptPattern {
  id: string
  name: string
  description: string
  tags: string[]
  type: "sine" | "square" | "sawtooth" | "custom"
  baseSpeed: number // strokes per second
  intensity: number // 0-100
  strokeLength: number // 0-100 (percentage of full stroke)
  customActions?: FunscriptAction[] // for custom patterns
  createdAt: number
  isBuiltIn: boolean
}

// Block editor types
export interface ScriptBlock {
  id: string
  name?: string // NEW: User-editable block name
  patternId: string
  duration: number // seconds
  repeatCount: number
  speedMultiplier: number
  intensityMultiplier: number
  strokeLengthMultiplier: number
  motionTrackerDraft?: any
  // Multi-axis layers - optional, additive. Each axis has its own pattern/actions.
  // If absent, block behaves exactly as before (stroke only)
  axisLayers?: Partial<Record<ScriptAxis, {
    patternId?: string          // pattern to use for this axis
    customActions?: FunscriptAction[] // or custom drawn actions
    enabled: boolean            // whether this axis is active in this block
    intensityMultiplier: number // 0-2, default 1
  }>>
}

export interface BlockTransition {
  type: "fade" | "blend" | "instant"
  duration: number // milliseconds
}

export type ContentType = "video" | "animation" | "image" | "audio"

export interface ScriptPricing {
  isPurchasable: boolean
  price: number // USD cents (e.g., 499 = $4.99)
  allowDownload: boolean // creator can disable download
  trialDurationSeconds: number // how long users can try before buying
}

export interface ScriptLicense {
  licenseId: string
  purchasedAt: number
  purchasedBy: string // userId
  transactionId?: string // for payment processor
}

// User script types
export interface UserScript {
  id: string
  name: string
  description: string
  contentId?: string // linked booru content ID (site:postId format)
  contentSite?: string // booru site name
  contentUrl?: string // content URL for preview
  thumbnailUrl?: string
  thumbnailPosition?: { x: number; y: number } // object-position for thumbnail crop (default: 50, 50)
  contentType?: ContentType
  funscript: Funscript
  blocks?: ScriptBlock[] // if created with block editor
  transitions?: BlockTransition[]
  createdAt: number
  updatedAt: number
  isPublished: boolean
  tags: string[]
  deviceCompatibility: string[]
  averageIntensity: number
  pricing?: ScriptPricing
  tagHint?: string

  // Pal Scripter local mirror fields. The real assignable package action
  // lives in pal_action_scripts; this ID lets the editor update that row.
  palActionScriptId?: string
  palVisibility?: "private" | "wip" | "active"
  
  // Audio source linking (SIB feature)
  audioSourceId?: string   // e.g. "audiochan:c9e1ac4e-..."
  audioSourceSite?: string // e.g. "audiochan"
  audioSourceTitle?: string // track title for display
  
  // Preserved copy of the funscript as it was when first imported/downloaded.
  // Never overwritten by autosave — used to restore the script to its original state.
  originalFunscript?: Funscript

  // Editor metadata - tracks which editor was used and its state
  editorMetadata?: {
    editorType: "script-creator" | "story-scripter" | "ab-loop" | "vertical-editor" | "curve-editor" | "block-editor" | "manual-editor" | "pal-scripter" | "imported"
    editorMode?: "curve" | "block" | "manual" | "vertical" // for script-creator sub-modes
    lastEditorState?: any // stores editor-specific state for restoration
  }
  
  // New fields for book scripts
  isBookScript?: boolean
  pages?: PageScript[]
  totalDuration?: number

  // Set when this script was opened from a purchased marketplace script (edit tier).
  // Blocks in-app republishing — purchased content can't be re-listed on the marketplace.
  purchasedFromScriptId?: string

  // Set when this script was created for a specific commission or bounty.
  commissionId?: string
  bountyId?: string

  // Carried over from the source commission/bounty's posting_rights at delivery time.
  // When "personal_only", this script can't be republished to the marketplace.
  postingRights?: "public" | "personal_only"

  // Carried over from the source commission's allow_download/allow_purchaser_edit at
  // delivery time. Undefined (e.g. bounties, or scripts not from a commission) means
  // unrestricted — only commission-delivered scripts with an explicit false are gated.
  commissionAllowDownload?: boolean
  commissionAllowEdit?: boolean

  // Multi-page gallery items (doujin/album/manga) carried over from the source
  // commission/bounty's content_media_items at delivery time.
  contentMediaItems?: OrderMediaItem[]
}

// Page script for book/doujin scripts
export interface PageScript {
  pageIndex: number
  blocks: ScriptBlock[]
  duration: number // seconds
  cumulativeTiming: number // milliseconds from start
  thumbnailUrl?: string // Optional page thumbnail
  transitions?: BlockTransition[] // Seam shaping between consecutive blocks
}

// Community script types
export interface CommunityScript extends UserScript {
  authorId: string
  authorName: string
  authorAvatar?: string
  rating: number // 1-5 stars
  ratingCount: number
  downloadCount: number
  favoriteCount: number
  isVerified: boolean
  reviews: ScriptReview[]
  licenses?: ScriptLicense[]
}

export interface ScriptReview {
  id: string
  authorId: string
  authorName: string
  rating: number
  comment: string
  createdAt: number
}

function splitContentKey(contentId?: string) {
  if (!contentId) return { site: undefined, id: undefined }
  const colonIndex = contentId.indexOf(":")
  if (colonIndex > 0) {
    return {
      site: contentId.slice(0, colonIndex),
      id: contentId.slice(colonIndex + 1),
    }
  }
  return { site: undefined, id: contentId }
}

function scriptMatchesContent(script: UserScript | CommunityScript, requestedContentId: string) {
  if (script.contentId === requestedContentId) return true

  const requested = splitContentKey(requestedContentId)
  const linked = splitContentKey(script.contentId)
  const scriptContentId = linked.id ?? script.contentId
  const scriptContentSite = script.contentSite ?? linked.site

  if (!requested.id || !scriptContentId || requested.id !== scriptContentId) return false
  return !requested.site || !scriptContentSite || requested.site === scriptContentSite
}

export interface UserAccount {
  userId: string
  username: string
  email?: string
  avatar?: string
  createdAt: number
  purchasedScripts: string[] // script IDs
  trialScripts: { scriptId: string; expiresAt: number }[]
}

// Store state
interface ScriptState {
  // User's local scripts
  scripts: UserScript[]
  // Downloaded community scripts
  downloadedScripts: CommunityScript[]
  // Favorite community scripts
  favoriteScriptIds: string[]
  // User's custom patterns
  customPatterns: ScriptPattern[]
  // Script ratings (user's ratings for community scripts)
  userRatings: Record<string, number>
  currentUser: UserAccount | null
  /**
   * The account the scripts saved in this browser belong to (null = made as a guest). See
   * lib/local-scripts-ownership.ts: it keeps one account's copy from showing to, or syncing into, another.
   */
  localOwnerId: string | null

  // Actions
  addScript: (script: Omit<UserScript, "id" | "createdAt" | "updatedAt">) => string
  upsertScript: (script: UserScript) => void
  updateScript: (id: string, updates: Partial<UserScript>) => void
  deleteScript: (id: string) => void
  duplicateScript: (id: string) => string | null
  resetScriptToOriginal: (id: string) => void

  addPattern: (pattern: Omit<ScriptPattern, "id" | "createdAt" | "isBuiltIn">) => string
  updatePattern: (id: string, updates: Partial<ScriptPattern>) => void
  deletePattern: (id: string) => void

  downloadCommunityScript: (script: CommunityScript) => void
  removeCommunityScript: (id: string) => void

  toggleFavoriteScript: (id: string) => void
  rateScript: (id: string, rating: number) => void

  getScriptById: (id: string) => UserScript | CommunityScript | undefined
  getScriptsForContent: (contentId: string) => (UserScript | CommunityScript)[]
  hasScriptsForContent: (contentId: string) => boolean

  setCurrentUser: (user: UserAccount | null) => void
  purchaseScript: (scriptId: string, transactionId?: string) => void
  startTrial: (scriptId: string, durationSeconds: number) => void
  hasLicense: (scriptId: string) => boolean
  isTrialActive: (scriptId: string) => boolean
  isTrialExpired: (scriptId: string) => boolean
  
  // Book script helper functions
  getBookScripts: (bookId: string) => UserScript[]
  isBookScript: (script: UserScript) => boolean
}

// Built-in patterns
export const BUILTIN_PATTERNS: ScriptPattern[] = [
  {
    id: "slow-rhythm",
    name: "Slow Rhythm",
    description: "Gentle sine wave motion",
    tags: ["gentle", "slow", "relaxing"],
    type: "sine",
    baseSpeed: 0.5,
    intensity: 40,
    strokeLength: 70,
    createdAt: 0,
    isBuiltIn: true,
  },
  {
    id: "fast-pump",
    name: "Fast Pump",
    description: "Rapid stroking motion",
    tags: ["intense", "fast", "energetic"],
    type: "square",
    baseSpeed: 3,
    intensity: 90,
    strokeLength: 50,
    createdAt: 0,
    isBuiltIn: true,
  },
  {
    id: "edge-mode",
    name: "Edge Mode",
    description: "Build to peak then stop",
    tags: ["edging", "tease", "denial"],
    type: "custom",
    baseSpeed: 1.5,
    intensity: 75,
    strokeLength: 80,
    createdAt: 0,
    isBuiltIn: true,
  },
  {
    id: "tease-denial",
    name: "Tease & Denial",
    description: "Build up then slow down",
    tags: ["tease", "denial", "slow-build"],
    type: "custom",
    baseSpeed: 1,
    intensity: 60,
    strokeLength: 75,
    createdAt: 0,
    isBuiltIn: true,
  },
  {
    id: "random-chaos",
    name: "Random Chaos",
    description: "Unpredictable intensity",
    tags: ["random", "unpredictable", "chaotic"],
    type: "custom",
    baseSpeed: 2,
    intensity: 70,
    strokeLength: 60,
    createdAt: 0,
    isBuiltIn: true,
  },
  {
    id: "pulse",
    name: "Pulse",
    description: "Rhythmic pulsing motion",
    tags: ["pulse", "rhythmic", "steady"],
    type: "square",
    baseSpeed: 1,
    intensity: 50,
    strokeLength: 40,
    createdAt: 0,
    isBuiltIn: true,
  },
  {
    id: "escalation",
    name: "Escalation",
    description: "Gradually increasing intensity",
    tags: ["buildup", "escalation", "intense"],
    type: "sawtooth",
    baseSpeed: 1,
    intensity: 80,
    strokeLength: 85,
    createdAt: 0,
    isBuiltIn: true,
  },
  {
    id: "wave",
    name: "Wave",
    description: "Smooth rolling motion",
    tags: ["smooth", "wave", "gentle"],
    type: "sine",
    baseSpeed: 0.75,
    intensity: 55,
    strokeLength: 90,
    createdAt: 0,
    isBuiltIn: true,
  },
]

function interpolatePatternPos(actions: { at: number; pos: number }[], timeMs: number): number {
  if (actions.length === 0) return 0
  if (timeMs <= actions[0].at) return actions[0].pos
  if (timeMs >= actions[actions.length - 1].at) return actions[actions.length - 1].pos
  let lo = 0
  let hi = actions.length - 1
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1
    if (actions[mid].at <= timeMs) lo = mid
    else hi = mid
  }
  const a = actions[lo]
  const b = actions[hi]
  const t = (timeMs - a.at) / (b.at - a.at)
  return a.pos + (b.pos - a.pos) * t
}

/** Synthesizes funscript actions from a built-in/custom pattern's sine/square/
 * sawtooth/custom motion definition — shared by the heatmap preview
 * (components/editors/global-script-player.tsx) and Pal Scriptor's builtin-
 * pattern chat-trigger fallback (lib/use-script-trigger-dispatch.ts), so both
 * stay in lockstep with a single source of truth. */
export function patternToFunscriptActions(pattern: ScriptPattern, cycles = 8): FunscriptAction[] {
  const periodMs = (1 / Math.max(0.05, pattern.baseSpeed)) * 1000
  const totalMs = periodMs * cycles
  const stepsPerCycle = 40
  const actions: FunscriptAction[] = []
  const amp = (pattern.strokeLength / 2) * (pattern.intensity / 100)
  const mid = 50

  for (let i = 0; i <= stepsPerCycle * cycles; i++) {
    const t = (i / (stepsPerCycle * cycles)) * totalMs
    const phase = ((t % periodMs) / periodMs) * Math.PI * 2
    let pos: number

    if (pattern.type === "sine") {
      pos = mid + Math.sin(phase) * amp
    } else if (pattern.type === "square") {
      pos = phase < Math.PI ? mid + amp : mid - amp
    } else if (pattern.type === "sawtooth") {
      pos = mid - amp + ((phase / (Math.PI * 2)) * amp * 2)
    } else if (pattern.customActions && pattern.customActions.length > 0) {
      const scaledT = (phase / (Math.PI * 2)) * (pattern.customActions[pattern.customActions.length - 1].at)
      pos = interpolatePatternPos(pattern.customActions, scaledT)
    } else {
      pos = mid + Math.sin(phase) * amp
    }

    actions.push({ at: Math.round(t), pos: Math.round(Math.max(0, Math.min(100, pos))) })
  }

  return actions
}

export const useScriptStore = create<ScriptState>()(
  persist(
    (set, get) => ({
      scripts: [],
      downloadedScripts: [],
      favoriteScriptIds: [],
      customPatterns: [],
      userRatings: {},
      currentUser: null,
      localOwnerId: null,

      addScript: (scriptData) => {
        const id = `script-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const now = Date.now()
        const newScript: UserScript = {
          ...scriptData,
          id,
          createdAt: now,
          updatedAt: now,
          // Preserve the initial funscript as the original so the user can always reset to it
          originalFunscript: scriptData.originalFunscript ?? scriptData.funscript,
        }
        set((state) => ({ scripts: [...state.scripts, newScript] }))
        return id
      },

      upsertScript: (script) => {
        const now = Date.now()
        const normalized: UserScript = {
          ...script,
          createdAt: script.createdAt ?? now,
          updatedAt: script.updatedAt ?? now,
          originalFunscript: script.originalFunscript ?? script.funscript,
        }
        set((state) => {
          const exists = state.scripts.some((s) => s.id === normalized.id)
          return {
            scripts: exists
              ? state.scripts.map((s) => (s.id === normalized.id ? { ...s, ...normalized } : s))
              : [...state.scripts, normalized],
          }
        })
      },

      updateScript: (id, updates) => {
        set((state) => ({
          scripts: state.scripts.map((s) => (s.id === id ? { ...s, ...updates, updatedAt: Date.now() } : s)),
        }))
      },

      deleteScript: (id) => {
        set((state) => ({
          scripts: state.scripts.filter((s) => s.id !== id),
        }))
      },

      duplicateScript: (id) => {
        const script = get().scripts.find((s) => s.id === id)
        if (!script) return null
        const newId = `script-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const now = Date.now()
        const newScript: UserScript = {
          ...script,
          id: newId,
          name: `${script.name} (Copy)`,
          createdAt: now,
          updatedAt: now,
          isPublished: false,
        }
        set((state) => ({ scripts: [...state.scripts, newScript] }))
        return newId
      },

      resetScriptToOriginal: (id) => {
        set((state) => ({
          scripts: state.scripts.map((s) => {
            if (s.id !== id || !s.originalFunscript) return s
            return {
              ...s,
              funscript: s.originalFunscript,
              editorMetadata: undefined,
              updatedAt: Date.now(),
            }
          }),
        }))
      },

      addPattern: (patternData) => {
        const id = `pattern-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const newPattern: ScriptPattern = {
          ...patternData,
          id,
          createdAt: Date.now(),
          isBuiltIn: false,
        }
        set((state) => ({ customPatterns: [...state.customPatterns, newPattern] }))
        return id
      },

      updatePattern: (id, updates) => {
        set((state) => ({
          customPatterns: state.customPatterns.map((p) => (p.id === id ? { ...p, ...updates } : p)),
        }))
      },

      deletePattern: (id) => {
        set((state) => ({
          customPatterns: state.customPatterns.filter((p) => p.id !== id),
        }))
      },

      downloadCommunityScript: (script) => {
        set((state) => {
          if (state.downloadedScripts.some((s) => s.id === script.id)) {
            return state
          }
          return { downloadedScripts: [...state.downloadedScripts, script] }
        })
      },

      removeCommunityScript: (id) => {
        set((state) => ({
          downloadedScripts: state.downloadedScripts.filter((s) => s.id !== id),
        }))
      },

      toggleFavoriteScript: (id) => {
        set((state) => {
          if (state.favoriteScriptIds.includes(id)) {
            return { favoriteScriptIds: state.favoriteScriptIds.filter((fid) => fid !== id) }
          }
          return { favoriteScriptIds: [...state.favoriteScriptIds, id] }
        })
      },

      rateScript: (id, rating) => {
        set((state) => ({
          userRatings: { ...state.userRatings, [id]: rating },
        }))
      },

      getScriptById: (id) => {
        const local = get().scripts.find((s) => s.id === id)
        if (local) return local
        return get().downloadedScripts.find((s) => s.id === id)
      },

      getScriptsForContent: (contentId) => {
        const local = get().scripts.filter((s) => scriptMatchesContent(s, contentId))
        const community = get().downloadedScripts.filter((s) => scriptMatchesContent(s, contentId))
        return [...local, ...community]
      },

      hasScriptsForContent: (contentId) => {
        const local = get().scripts.some((s) => scriptMatchesContent(s, contentId))
        const community = get().downloadedScripts.some((s) => scriptMatchesContent(s, contentId))
        return local || community
      },

      setCurrentUser: (user) => {
        set({ currentUser: user })
      },

      purchaseScript: (scriptId, transactionId) => {
        set((state) => {
          if (!state.currentUser) return state
          if (state.currentUser.purchasedScripts.includes(scriptId)) return state
          return {
            currentUser: {
              ...state.currentUser,
              purchasedScripts: [...state.currentUser.purchasedScripts, scriptId],
              // Remove from trials if purchased
              trialScripts: state.currentUser.trialScripts.filter((t) => t.scriptId !== scriptId),
            },
          }
        })
      },

      startTrial: (scriptId, durationSeconds) => {
        set((state) => {
          if (!state.currentUser) return state
          // Check if already in trial or purchased
          if (state.currentUser.purchasedScripts.includes(scriptId)) return state
          if (state.currentUser.trialScripts.some((t) => t.scriptId === scriptId)) return state
          return {
            currentUser: {
              ...state.currentUser,
              trialScripts: [
                ...state.currentUser.trialScripts,
                { scriptId, expiresAt: Date.now() + durationSeconds * 1000 },
              ],
            },
          }
        })
      },

      hasLicense: (scriptId) => {
        const user = get().currentUser
        if (!user) return false
        return user.purchasedScripts.includes(scriptId)
      },

      isTrialActive: (scriptId) => {
        const user = get().currentUser
        if (!user) return false
        const trial = user.trialScripts.find((t) => t.scriptId === scriptId)
        if (!trial) return false
        return trial.expiresAt > Date.now()
      },

      isTrialExpired: (scriptId) => {
        const user = get().currentUser
        if (!user) return false
        const trial = user.trialScripts.find((t) => t.scriptId === scriptId)
        if (!trial) return false
        return trial.expiresAt <= Date.now()
      },
      
      // Book script helper functions
      getBookScripts: (bookId) => {
        const scripts = get().scripts
        return scripts.filter((script) => 
          script.isBookScript === true && script.contentId === bookId
        )
      },
      
      isBookScript: (script) => {
        return script.isBookScript === true && Array.isArray(script.pages)
      },
    }),
    {
      name: "script-storage",
      storage: {
        getItem: (name) => {
          try {
            const raw = localStorage.getItem(name)
            return raw ? JSON.parse(raw) : null
          } catch {
            return null
          }
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, JSON.stringify(value))
          } catch (e) {
            if (e instanceof DOMException && (e.name === "QuotaExceededError" || e.code === 22)) {
              // Storage full — prune the oldest scripts down to 10 and retry once
              try {
                const stored = localStorage.getItem(name)
                if (stored) {
                  const parsed = JSON.parse(stored)
                  if (Array.isArray(parsed?.state?.scripts) && parsed.state.scripts.length > 10) {
                    parsed.state.scripts = (parsed.state.scripts as UserScript[])
                      .sort((a, b) => b.updatedAt - a.updatedAt)
                      .slice(0, 10)
                    localStorage.setItem(name, JSON.stringify(parsed))
                  }
                }
              } catch {
                // If we still can't write, just skip persisting this update
              }
            }
          }
        },
        removeItem: (name) => {
          try {
            localStorage.removeItem(name)
          } catch {}
        },
      },
    },
  ),
)
